import { sessionApi } from './session.js';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

const BASE = '/api';

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Внутренний флаг: повтор после обновления токена делается ровно один раз. */
  retried?: boolean;
  signal?: AbortSignal;
}

async function parseError(response: Response): Promise<ApiError> {
  let code = 'unknown';
  let message = response.statusText;
  let details: unknown;

  try {
    const payload: unknown = await response.json();
    if (typeof payload === 'object' && payload !== null && 'error' in payload) {
      const err = (payload as { error: { code?: string; message?: string; details?: unknown } })
        .error;
      code = err.code ?? code;
      message = err.message ?? message;
      details = err.details;
    }
  } catch {
    // Тело не JSON — оставляем статус как есть.
  }

  return new ApiError(response.status, code, message, details);
}

let refreshInFlight: Promise<boolean> | null = null;

/** Обновление токена делается один раз на все параллельные запросы. */
async function refreshToken(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const response = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!response.ok) return false;

      const payload: unknown = await response.json();
      if (
        typeof payload === 'object' &&
        payload !== null &&
        'accessToken' in payload &&
        'actor' in payload
      ) {
        const typed = payload as { accessToken: string; actor: Parameters<typeof sessionApi.set>[1] };
        sessionApi.set(typed.accessToken, typed.actor);
        return true;
      }
      return false;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const token = sessionApi.token();

  const response = await fetch(`${BASE}${path}`, {
    method: options.method ?? 'GET',
    credentials: 'include',
    headers: {
      ...(options.body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...(token === null ? {} : { Authorization: `Bearer ${token}` }),
    },
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    ...(options.signal === undefined ? {} : { signal: options.signal }),
  });

  if (response.status === 401 && options.retried !== true) {
    if (await refreshToken()) {
      return api<T>(path, { ...options, retried: true });
    }
    sessionApi.clear();
  }

  if (!response.ok) throw await parseError(response);
  if (response.status === 204) return undefined as T;

  return (await response.json()) as T;
}

/**
 * Загрузка файла НАПРЯМУЮ в хранилище по подписанной ссылке.
 * Версию объекта забираем из заголовка ответа: Object Lock защищает версии,
 * а не имена, и в записи осмотра надо закрепить именно загруженную.
 */
export async function uploadToStorage(
  url: string,
  bytes: Uint8Array,
  contentType: string,
): Promise<{ versionId: string | null }> {
  const copy = new Uint8Array(bytes);
  const response = await fetch(url, {
    method: 'PUT',
    body: copy,
    headers: { 'Content-Type': contentType },
  });

  if (!response.ok) {
    throw new ApiError(response.status, 'upload_failed', 'Не удалось загрузить файл');
  }

  return { versionId: response.headers.get('x-amz-version-id') };
}
